import { renderSeed } from './missions.js';
/**
 * The values a learner has to find on this level and has not been shown, for
 * the mentor's output filter (worker/mentor-filter.ts).
 *
 * ## Why the tab computes this and the Worker does not
 *
 * The filter is meant to hold back a value the learner must find — the commit
 * a bisect lands on, the PID of the process writing the file, the port the
 * server is really on — even when it is sitting on the learner's own screen,
 * because finding it among the rest IS the task. Since prompt v3 the model
 * reads the terminal and the files, so a learner who ran `git log --oneline`
 * on l17-who-broke-it has handed it the very hash `ANSWER.md` is checked
 * against, and only an instruction stood between that and "it's ca3965d".
 *
 * The Worker cannot hold these by itself. Its bundles are built from
 * `missions/` (scripts/build-mentor-bundles.mjs), and seed values do not exist
 * there: they are what a seed script printed as `@@KEY=value` when the
 * workspace was made, and a level refers to them as `{{seed.KEY}}`. So every
 * bundle's `secrets` is solution lines and nothing else. The session in this
 * tab is the one place that holds both the level and the values it was
 * seeded with, and this is the question asked there.
 *
 * Pure, on purpose: no workspace, no I/O, nothing but the mission and the
 * seed, so asking it costs a mentor turn nothing.
 */
/**
 * The shortest value worth holding back.
 *
 * The same floor as `MIN_MATCH` in worker/mentor-filter.ts and `MIN_SECRET`
 * in scripts/mentor-secret-key.mjs, for their reason: a two-letter value is
 * one the learner could not have failed to find, and a filter that rejected
 * on it would reject every reply that happened to contain those letters.
 */
export const MIN_HIDDEN_CHARS = 4;
/**
 * The longest. A seed value is a hash, a number, a name or a path; a value
 * longer than this is a blob, and a reply that quoted two hundred characters
 * of one verbatim is not the failure this exists for.
 */
export const MAX_HIDDEN_CHARS = 200;
/** How many are sent with one question. Far above any level's seed; a ceiling, not a budget. */
export const MAX_HIDDEN = 40;
/** Case-folded, every run of whitespace one space: the filter's `flatten`, by agreement. */
function flatten(text) {
    return text.replace(/\s+/g, ' ').trim().toLowerCase();
}
/**
 * Every `with.detail` in a check, and in the checks inside it.
 *
 * Nested because `all_of` returns its first failing sub-check's outcome
 * whole, and `any_of` its first failure that said something, so the sentence
 * a learner reads under a failing objective with no `fail:` of its own can be
 * a sub-check's `detail` two levels down.
 */
function details(spec, out) {
    const w = spec?.with;
    if (!w || typeof w !== 'object')
        return;
    if (typeof w.detail === 'string')
        out.push(w.detail);
    if (!Array.isArray(w.checks))
        return;
    for (const sub of w.checks) {
        if (sub && typeof sub === 'object')
            details(sub, out);
    }
}
/**
 * The prose a learner reads before passing, still templated.
 *
 * This mirrors `visibleText` in scripts/mentor-secrets.mjs — the build's
 * answer to "what has the learner already been told" — and differs from it in
 * three places, each of which makes the list longer rather than shorter:
 *
 * - **The reflection is not in it.** It is shown after a pass, so while
 *   anybody is asking the mentor for help it has not been read. The build
 *   counts it because its question is about authored prose the model may
 *   repeat; this one is about values the learner has not seen yet.
 * - **A hidden objective contributes nothing**: not its title, and not its
 *   `fail`, `detail` or diagnostics either. Objectives.tsx draws a hidden
 *   objective only at the moment its check fails, title and message together,
 *   so until the learner trips it none of that text has been on screen.
 * - **Nested checks' `detail` is in it** (`details`, above), which the build's
 *   one-level read of `with.detail` does not reach.
 *
 * The warm-up is not in it either: it is offered before the level and never
 * required, so a value named only there is not one every learner has read.
 */
function visibleProse(mission) {
    const parts = [mission.brief ?? '', ...(mission.constraints ?? [])];
    for (const o of mission.objectives ?? []) {
        if (o.hidden === true)
            continue;
        parts.push(o.title ?? '', o.fail ?? '');
        details(o, parts);
        for (const d of o.diagnostics ?? []) {
            parts.push(d.message ?? '');
            details(d, parts);
        }
    }
    return parts;
}
/**
 * The seed values this learner has not been shown: at least
 * `MIN_HIDDEN_CHARS` and at most `MAX_HIDDEN_CHARS` long, absent from every
 * piece of the level's visible prose once it is templated with THIS seed,
 * compared case-folded with whitespace collapsed, deduplicated, and at most
 * `MAX_HIDDEN` of them.
 *
 * Templated with this seed because that is what the screen shows: a brief
 * that says `{{seed.BRANCH}}` has told the learner the branch, and a brief
 * that shows `{{seed.BAD}}` has told them every prefix of it too, including
 * the short hash another key holds. A reference with no value is left as it
 * stands (`renderSeed`, the lenient renderer prose is drawn with), because a
 * placeholder the screen could not fill named nothing.
 *
 * Each piece is searched on its own, so a value cannot be found straddling
 * the end of one sentence and the start of the next — the same rule the
 * filter keeps for the learner's evidence.
 *
 * Seed NAMES are not returned. The build's secret rule counts them because a
 * name can leak into prose an author wrote; a key such as `BAD_SHORT` is not
 * something anybody has to find.
 */
export function hiddenSeedValues(mission, seed) {
    const visible = renderSeed(visibleProse(mission), seed ?? {})
        .map(flatten)
        .filter((text) => text !== '');
    const seen = new Set();
    const out = [];
    for (const raw of Object.values(seed ?? {})) {
        if (out.length >= MAX_HIDDEN)
            break;
        if (typeof raw !== 'string')
            continue;
        const value = raw.trim();
        if (value.length > MAX_HIDDEN_CHARS)
            continue;
        const key = flatten(value);
        if (key.length < MIN_HIDDEN_CHARS || seen.has(key))
            continue;
        seen.add(key);
        if (visible.some((text) => text.includes(key)))
            continue;
        out.push(value);
    }
    return out;
}
/** What a withheld value reads as in a check's output. Square brackets, not angle: nothing here may look like a fence. */
export const WITHHELD = '[withheld]';
/**
 * A check's output with every hidden value blanked, case-insensitively.
 *
 * ## Why check output, and only check output
 *
 * A failing check's output reaches the model as evidence, and a check was
 * written when nothing read what it printed. An audit of the course found 32
 * visible objectives whose failure prints the value the learner is working
 * towards — `want 12345, got 12000` on l81's bill, l82's charges, l91's
 * arithmetic — and the learner never sees that line: the screen shows the
 * objective's sentence, not the script's stdout. The Worker's filter would
 * stop a reply that repeated `12345`, but not one that said "about twelve
 * thousand three hundred", and the model had been handed the number. Blanked
 * here, `want [withheld], got 12000` still tells the model the total is wrong
 * and what the learner's code produced, which is the diagnosis; it no longer
 * tells it the answer.
 *
 * The terminal and the learner's files are NOT blanked. Those are the
 * learner's own screen, and a value on it is one they can read — the filter
 * still holds it back from a reply, because seeing a hash in a `git log` is
 * not the same as having found which one it is.
 *
 * Longest first, so a full hash is blanked whole before the short hash that
 * is its prefix can leave the rest of it behind.
 */
export function withholdHidden(text, hidden) {
    let out = text;
    const values = hidden.filter((v) => typeof v === 'string' && v.trim().length >= MIN_HIDDEN_CHARS)
        .map((v) => v.trim())
        .sort((a, b) => b.length - a.length);
    for (const value of values) {
        const lower = value.toLowerCase();
        const haystack = out.toLowerCase();
        // Case-folding that changes a string's length (a dotted capital I is two
        // code units lowered) would put the indices below out of step with the
        // text they cut, so such output is matched exactly as it is instead.
        if (haystack.length !== out.length || lower.length !== value.length) {
            out = out.split(value).join(WITHHELD);
            continue;
        }
        let from = 0;
        let result = '';
        for (;;) {
            const at = haystack.indexOf(lower, from);
            if (at < 0)
                break;
            result += out.slice(from, at) + WITHHELD;
            from = at + value.length;
        }
        out = result + out.slice(from);
    }
    return out;
}
//# sourceMappingURL=mentor-hidden.js.map